import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";

function Finalists() {
  const [finalists, setFinalists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [rankFilter, setRankFilter] = useState("all"); // all, top10, top20
  const [sortBy, setSortBy] = useState("rank");
  const [sortOrder, setSortOrder] = useState("asc");

  const { apiBaseUrl } = useAuth();

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token") || "";

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  async function fetchFinalists() {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${apiBaseUrl}/api/final/finalists`, {
        headers,
      });
      if (!res.ok) throw new Error("خطا در دریافت فینالیست‌ها");
      const json = await res.json();
      console.log("Finalists Response:", json);
      setFinalists(json?.items || []);
    } catch (e) {
      setError(e.message || "خطای نامشخص");
      console.error("Error fetching finalists:", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchFinalists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRow = (userId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const processedFinalists = useMemo(() => {
    let result = [...finalists];

    // Search Filter
    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase();
      result = result.filter((f) => {
        const fullName = `${f.first_name} ${f.last_name}`.toLowerCase();
        const email = (f.email || "").toLowerCase();
        const username = (f.username || "").toLowerCase();
        return (
          fullName.includes(search) ||
          email.includes(search) ||
          username.includes(search) ||
          String(f.user_id).includes(search) ||
          String(f.rank).includes(search) ||
          String(f.phone_number).includes(search)
        );
      });
    }

    // Status Filter
    if (statusFilter === "active") {
      result = result.filter((f) => f.is_active);
    } else if (statusFilter === "inactive") {
      result = result.filter((f) => !f.is_active);
    }

    // Rank Filter
    if (rankFilter === "top10") {
      result = result.filter((f) => parseInt(f.rank) <= 10);
    } else if (rankFilter === "top20") {
      result = result.filter((f) => parseInt(f.rank) <= 20);
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "rank":
          aVal = parseInt(a.rank) || 999;
          bVal = parseInt(b.rank) || 999;
          break;
        case "points":
          aVal = parseFloat(a.points) || 0;
          bVal = parseFloat(b.points) || 0;
          break;
        case "balance":
          aVal = parseFloat(a.balance) || 0;
          bVal = parseFloat(b.balance) || 0;
          break;
        case "equity":
          aVal = parseFloat(a.equity) || 0;
          bVal = parseFloat(b.equity) || 0;
          break;
        case "stage_3_score":
          aVal = parseFloat(a.stage_3_score) || 0;
          bVal = parseFloat(b.stage_3_score) || 0;
          break;
        case "name":
          aVal = `${a.first_name} ${a.last_name}`;
          bVal = `${b.first_name} ${b.last_name}`;
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal, "fa")
            : bVal.localeCompare(aVal, "fa");
        default:
          return 0;
      }

      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [finalists, searchQuery, statusFilter, rankFilter, sortBy, sortOrder]);

  const getRankBadgeColor = (rank) => {
    const r = parseInt(rank);
    if (r === 1) return "bg-yellow-500 text-white";
    if (r === 2) return "bg-gray-400 text-white";
    if (r === 3) return "bg-orange-600 text-white";
    if (r <= 10) return "bg-emerald-500 text-white";
    return "bg-blue-500 text-white";
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setRankFilter("all");
    setSortBy("rank");
    setSortOrder("asc");
  };

  const SortButton = ({ column, label }) => (
    <button
      onClick={() => {
        if (sortBy === column) {
          setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
          setSortBy(column);
          setSortOrder("asc");
        }
      }}
      className="flex items-center gap-1 hover:text-purple-400 transition-colors"
    >
      {label}
      {sortBy === column && (
        <span className="text-purple-400">
          {sortOrder === "asc" ? "↑" : "↓"}
        </span>
      )}
    </button>
  );

  return (
    <div className="p-6" style={{ direction: "rtl" }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#8b5cf6" }}
          >
            <span className="text-white text-xl">🏆</span>
          </div>
          <div>
            <h2 className="text-white text-xl font-bold">لیست فینالیست‌ها</h2>
            <p className="text-gray-400 text-xs">
              {processedFinalists.length} از {finalists.length} فینالیست
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={clearFilters}
            className="px-4 py-2 rounded-xl text-white font-medium bg-gray-700 hover:bg-gray-600 transition-colors"
          >
            🔄 پاک کردن فیلترها
          </button>
          <button
            onClick={fetchFinalists}
            className="px-4 py-2 rounded-xl text-white font-medium bg-purple-600 hover:bg-purple-700 transition-colors"
          >
            🔄 بروزرسانی
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              جستجو
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="نام، ایمیل، تلفن، user_id، رتبه..."
                className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-700 text-gray-200 focus:outline-none focus:border-purple-500"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔎</span>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              وضعیت
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-700 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="all">همه</option>
              <option value="active">فعال</option>
              <option value="inactive">غیرفعال</option>
            </select>
          </div>

          {/* Rank Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              رتبه
            </label>
            <select
              value={rankFilter}
              onChange={(e) => setRankFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-700 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="all">همه</option>
              <option value="top10">10 نفر برتر</option>
              <option value="top20">20 نفر برتر</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-16 text-center text-gray-300">
          در حال بارگذاری...
        </div>
      ) : error ? (
        <div className="py-6 text-center text-red-400">{error}</div>
      ) : processedFinalists.length === 0 ? (
        <div className="py-16 text-center text-gray-300">
          {searchQuery || statusFilter !== "all" || rankFilter !== "all"
            ? "فینالیستی با این فیلترها یافت نشد"
            : "فینالیستی یافت نشد"}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr className="text-gray-300 text-sm">
                  <th className="px-4 py-3 text-center w-12">جزئیات</th>
                  <th className="px-4 py-3 text-right">
                    <SortButton column="rank" label="رتبه" />
                  </th>
                  <th className="px-4 py-3 text-right">
                    <SortButton column="name" label="نام" />
                  </th>
                  <th className="px-4 py-3 text-right">User ID</th>
                  <th className="px-4 py-3 text-right">
                    <SortButton column="points" label="امتیاز" />
                  </th>
                  <th className="px-4 py-3 text-right">
                    <SortButton column="balance" label="موجودی" />
                  </th>
                  <th className="px-4 py-3 text-right">
                    <SortButton column="equity" label="Equity" />
                  </th>
                  <th className="px-4 py-3 text-right">
                    <SortButton column="stage_3_score" label="امتیاز مرحله 3" />
                  </th>
                  <th className="px-4 py-3 text-center">وضعیت</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {processedFinalists.map((finalist) => (
                  <React.Fragment key={finalist.user_id}>
                    {/* Main Row */}
                    <tr className="hover:bg-gray-750 transition-colors">
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleRow(finalist.user_id)}
                          className="text-purple-400 hover:text-purple-300 transition-colors"
                        >
                          {expandedRows.has(finalist.user_id) ? "▼" : "◀"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center justify-center px-3 py-1 rounded-lg font-bold text-sm ${getRankBadgeColor(
                            finalist.rank
                          )}`}
                        >
                          #{finalist.rank}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">
                          {finalist.first_name} {finalist.last_name}
                        </div>
                        <div className="text-gray-400 text-xs mt-1">
                          {finalist.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 font-mono">
                        {finalist.user_id}
                      </td>
                      <td className="px-4 py-3 text-emerald-400 font-bold">
                        {finalist.points}
                      </td>
                      <td className="px-4 py-3 text-blue-400 font-mono">
                        ${parseFloat(finalist.balance).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-purple-400 font-mono">
                        ${parseFloat(finalist.equity).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-orange-400 font-mono">
                        {parseFloat(finalist.stage_3_score).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            finalist.is_active
                              ? "bg-emerald-500/20 text-emerald-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {finalist.is_active ? "✅ فعال" : "❌ غیرفعال"}
                        </span>
                      </td>
                    </tr>

                    {/* Expanded Row */}
                    {expandedRows.has(finalist.user_id) && (
                      <tr className="bg-gray-900">
                        <td colSpan="9" className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Personal Info */}
                            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                              <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                                👤 اطلاعات شخصی
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">نام کاربری:</span>
                                  <span className="text-gray-200 font-mono">
                                    {finalist.username}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">ایمیل:</span>
                                  <span className="text-gray-200" dir="ltr">
                                    {finalist.email}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">شماره تماس:</span>
                                  <span className="text-gray-200 font-mono" dir="ltr">
                                    {finalist.phone_number}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Account Info */}
                            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                              <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                                💼 اطلاعات حساب
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Account ID:</span>
                                  <span className="text-gray-200 font-mono">
                                    {finalist.account_id}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Account Login:</span>
                                  <span className="text-gray-200 font-mono">
                                    {finalist.account_login}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Competition ID:</span>
                                  <span className="text-gray-200 font-mono">
                                    {finalist.competition_id}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Stage Info */}
                            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                              <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                                🎯 اطلاعات مرحله
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Stage ID:</span>
                                  <span className="text-gray-200 font-mono">
                                    {finalist.stage_id}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Stage Number:</span>
                                  <span className="text-gray-200 font-mono">
                                    {finalist.stage_number}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">
                                    رتبه مرحله 3:
                                  </span>
                                  <span className="text-orange-400 font-bold">
                                    #{finalist.stage_3_final_global_rank_in_level}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && !error && finalists.length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">کل</p>
            <p className="text-white text-2xl font-bold">{finalists.length}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">نمایش</p>
            <p className="text-purple-400 text-2xl font-bold">
              {processedFinalists.length}
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">فعال</p>
            <p className="text-emerald-400 text-2xl font-bold">
              {finalists.filter((f) => f.is_active).length}
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">غیرفعال</p>
            <p className="text-red-400 text-2xl font-bold">
              {finalists.filter((f) => !f.is_active).length}
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">10 نفر برتر</p>
            <p className="text-yellow-400 text-2xl font-bold">
              {finalists.filter((f) => parseInt(f.rank) <= 10).length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Finalists;